import React, { useState, useEffect } from "react";
import { Box, TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ChatHeader } from "./ChatHeader/chatHeader";
import { MessageContainer } from "./MessageContainer/messageContainer";
import { useMutation } from "@apollo/client";
import { MARK_CHAT_MESSAGES_AS_READ } from "../../../../apollo-client/mutations";
import { ChatInputBar } from "./MessageContainer/chat-input-bar";
import { useIntlId } from "../../../../hooks/use-intl-id";
import { GET_TOTAL_UNREAD_MESSAGES_COUNT } from "../../courseNav/small-course-nav/getTotalUnreadMessagesCountQuery";
import { useChatStore } from "../../../../store/chatStore";

export const MainChatWindow = () => {
  const [postingMessage, setPostingMessage] = useState("");

  const searchPlaceholder = useIntlId("chat.search-for-contact-placeholder");

  // Use centralized chat store
  const {
    chosenChat,
    searchContact,
    setSearchContact,
    markMessagesAsRead,
  } = useChatStore();

  const [markChatMessagesAsRead] = useMutation(MARK_CHAT_MESSAGES_AS_READ);
  
  const handleMarkMessagesAsRead = async (chosenChatId) => {
    await markChatMessagesAsRead({
      variables: {
        id: chosenChatId || chosenChat?.chatId,
      },
      refetchQueries: [GET_TOTAL_UNREAD_MESSAGES_COUNT],
    });
    
    // Also update store state
    if (chosenChatId || chosenChat?.chatId) {
      await markMessagesAsRead(chosenChatId || chosenChat.chatId);
    }
  };

  // Automatically add the message to the chat when posting
  useEffect(() => {
    if (postingMessage && chosenChat?.chatId) {
      const newMessage = {
        _id: `temp-${Date.now()}`, // Temporary ID
        author: { username: "You" }, // Replace with actual user data
        isAuthor: true,
        message: postingMessage,
        createdAt: Date.now(),
        wasDelivered: false,
        wasRead: false,
      };
      
      // Update the chosenChat in store with new message
      if (chosenChat.messages) {
        chosenChat.messages = [...chosenChat.messages, newMessage];
      } else {
        chosenChat.messages = [newMessage];
      }
    }
  }, [postingMessage, chosenChat]);

  // Don't render if no chat is selected
  if (!chosenChat) {
    return null;
  }

  return (
    <MainChatWrapper
      onClick={async () => {
        if (chosenChat.unreadMessagesCount > 0) {
          await handleMarkMessagesAsRead(chosenChat._id);
        }
      }}
    >
      <SearchBar
        placeholder={searchPlaceholder}
        onChange={(event) => {
          setSearchContact(event.target.value);
        }}
        variant="standard"
        sx={{
          width: "calc(100% - 20px)",
          marginTop: "23px",
          marginLeft: "10px",
          marginRight: "10px",
          fontSize: "16px",
          "& .MuiInput-underline:before": { borderBottom: "none" },
          "& .MuiInput-underline:after": { borderBottom: "none" },
          "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottom: "none" },
          "& .MuiInput-underline.Mui-focused:after": { borderBottom: "1px solid lightgrey" },
          "& .MuiInput-underline": { borderBottom: "1px solid lightgrey" },
        }}
      />
      <ChatHeader />
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <MessageContainer
          chosenChat={chosenChat}
          postingMessage={postingMessage} // Pass the posting message for optimistic rendering
        />
      </Box>
      <ChatInputBar
        chosenChat={chosenChat}
        setPostingMessage={setPostingMessage} // Clear input and trigger optimistic response
      />
    </MainChatWrapper>
  );
};

const MainChatWrapper = styled(Box)({
  position: "relative",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  alignItems: "stretch",
  height: "100%",
  minHeight: 0, // Allow container to shrink
});

const SearchBar = styled(TextField)({});
